import {
  Box,
  Button,
  Flex,
  Text,
  useDisclosure,
  VStack,
  Image,
  Icon,
  Collapsible,
  Separator,
} from "@chakra-ui/react";
import { Tooltip } from "../ui/tooltip";
import { IconType } from "react-icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import FavIcon from "../../assets/resultng-favicon.png";
import { Dispatch, useEffect, useState } from "react";
import { useUserContext } from "../../Contexts/userContext";
import useThemeColors from "../../hooks/useThemeColors";
import MobileMenu from "./MobileMenu";
import useIcons from "../../hooks/useIcons";
import { useStyles } from "../../hooks/useStyles";
import darkModeLogo from "../../assets/resultng-logo-dark.png";
import { useBreakPoint } from "../../hooks/useBreakPoint";

interface Props {
  isCollapsed: boolean;
  setIsCollapsed: Dispatch<React.SetStateAction<boolean>>;
  onToggleCollapse: () => void;
  pageName: string;
}

function NavMenu({ isCollapsed, onToggleCollapse }: Props) {
  interface NavLinks {
    Name: string;
    Link: string;
    Icon: IconType;
    badge?: string;
  }

  const icons = useIcons();

  // Core navigation - accessible to all users
  const coreNavLinks: NavLinks[] = [
    { Name: "Dashboard", Link: "/", Icon: icons.main.home },
    { Name: "Students", Link: "/students", Icon: icons.people.students },
    { Name: "Results", Link: "/results", Icon: icons.main.result },
  ];

  // Admin Hub sub-navigation groups
  const schoolManagementLinks: NavLinks[] = [
    { Name: "School Settings", Link: "/admin/school", Icon: icons.main.school },
  ];

  const academicManagementLinks: NavLinks[] = [
    {
      Name: "Sessions",
      Link: "/admin/academic/sessions",
      Icon: icons.main.session,
    },
    {
      Name: "Terms",
      Link: "/admin/academic/terms",
      Icon: icons.main.term,
    },
    {
      Name: "Levels",
      Link: "/admin/academic/levels",
      Icon: icons.main.level,
    },
    {
      Name: "Classes",
      Link: "/admin/academic/classes",
      Icon: icons.main.class,
    },
    {
      Name: "Subjects",
      Link: "/admin/academic/subjects",
      Icon: icons.main.subject,
    },
    {
      Name: "Assessment Types",
      Link: "/admin/academic/assessment-types",
      Icon: icons.main.assessment,
    },
    {
      Name: "Grade Criteria",
      Link: "/admin/academic/grades",
      Icon: icons.main.grade,
    },
  ];

  const userManagementLinks: NavLinks[] = [
    { Name: "Users & Roles", Link: "/admin/users", Icon: icons.people.user },
    { Name: "Audit Trail", Link: "/admin/audit-trail", Icon: icons.main.audit },
    {
      Name: "Notifications",
      Link: "/admin/notifications",
      Icon: icons.main.notifications,
    },
  ];

  const dataOperationsLinks: NavLinks[] = [
    {
      Name: "Export Data",
      Link: "/admin/data/export",
      Icon: icons.utility.upload,
    },
    {
      Name: "Delete Data",
      Link: "/admin/data/delete",
      Icon: icons.utility.delete,
    },
    {
      Name: "Bulk Reports",
      Link: "/admin/data/bulk-reports",
      Icon: icons.utility.print,
    },
  ];

  const promotionLinks: NavLinks[] = [
    {
      Name: "Promotion System",
      Link: "/admin/promotion",
      Icon: icons.main.promotion,
    },
  ];

  const location = useLocation();
  const colors = useThemeColors();
  const { open, onClose } = useDisclosure();
  const { isMobile } = useBreakPoint();
  const { user } = useUserContext();
  const { noBarScrollBar } = useStyles();

  // Collapsible state for admin hub groups
  const [expandedGroups, setExpandedGroups] = useState<{
    academic: boolean;
    users: boolean;
    data: boolean;
  }>({
    academic: false,
    users: false,
    data: false,
  });

  // State to control admin navigation visibility
  const [showAdminNav, setShowAdminNav] = useState(false);

  const navigate = useNavigate();

  const handleLogoClick = () => {
    navigate("/");
  };

  const toggleSidebar = () => {
    onToggleCollapse();
  };

  const toggleAdminNav = () => {
    setShowAdminNav(!showAdminNav);
  };

  const isActiveLink = (link: string): boolean => {
    return location.pathname === link;
  };

  const isInAdminSection = (): boolean => {
    return location.pathname.startsWith("/admin");
  };

  const toggleGroup = (group: keyof typeof expandedGroups) => {
    setExpandedGroups((prev) => {
      const newState = {
        academic: false,
        users: false,
        data: false,
      };
      // Only open the clicked group, close all others
      newState[group] = !prev[group];
      return newState;
    });
  };

  const isAdmin = user?.role === 3;

  // Auto-show admin nav when on admin page
  useEffect(() => {
    if (isAdmin && isInAdminSection()) {
      setShowAdminNav(true);
    }
  }, [location.pathname, isAdmin]);

  // Close admin nav when clicking core navigation
  useEffect(() => {
    if (!isInAdminSection()) {
      setShowAdminNav(false);
    }
  }, [location.pathname]);

  // Render admin hub sub-navigation
  const renderAdminHubGroup = (
    title: string,
    links: NavLinks[],
    groupKey: keyof typeof expandedGroups | "school" | "promotion",
    icon: IconType,
    isExpanded: boolean
  ) => {
    // If there's only one link, render it as a direct link instead of an accordion
    if (links.length === 1) {
      const nav = links[0];
      if (isCollapsed) {
        return (
          <Tooltip
            content={nav.Name}
            positioning={{ placement: "right" }}
            showArrow
          >
            <Button
              asChild
              size="sm"
              variant="ghost"
              color="white"
              opacity={location.pathname === nav.Link ? "1" : "0.7"}
              bg={
                location.pathname === nav.Link
                  ? colors.brand.navPrimaryButton
                  : "transparent"
              }
              _hover={{ bg: colors.brand.navButtonHover }}
            >
              <Link to={nav.Link}>
                <Icon as={nav.Icon} />
              </Link>
            </Button>
          </Tooltip>
        );
      }

      return (
        <Button
          fontSize="sm"
          fontWeight="normal"
          justifyContent="flex-start"
          variant="ghost"
          asChild
          opacity={location.pathname === nav.Link ? "1" : "0.7"}
          bg={
            location.pathname === nav.Link
              ? colors.brand.navPrimaryButton
              : "transparent"
          }
          _hover={{
            bg:
              location.pathname === nav.Link
                ? colors.brand.navPrimaryButton
                : colors.brand.navButtonHover,
            color: "white",
          }}
          borderRadius="md"
          px={3}
          py={2}
          color="white"
        >
          <Link to={nav.Link}>
            <Icon as={icon} boxSize={4} /> {nav.Name}
          </Link>
        </Button>
      );
    }

    // For multiple links, render as accordion
    if (isCollapsed) {
      return (
        <VStack gap={1} align="center">
          {links.map((nav) => (
            <Tooltip
              content={nav.Name}
              positioning={{ placement: "right" }}
              showArrow
              key={nav.Link}
            >
              <Button
                asChild
                size="sm"
                variant="ghost"
                color="white"
                opacity={location.pathname === nav.Link ? "1" : "0.7"}
                bg={
                  location.pathname === nav.Link
                    ? colors.brand.navPrimaryButton
                    : "transparent"
                }
                _hover={{ bg: colors.brand.navButtonHover }}
              >
                <Link to={nav.Link}>
                  <Icon as={nav.Icon} size="sm" />
                </Link>
              </Button>
            </Tooltip>
          ))}
        </VStack>
      );
    }

    return (
      <VStack gap={1} align="stretch" w="full">
        <Button
          fontSize="sm"
          fontWeight="normal"
          justifyContent="flex-start"
          variant="ghost"
          onClick={() => toggleGroup(groupKey as keyof typeof expandedGroups)}
          opacity={
            isExpanded && links.some((link) => location.pathname === link.Link)
              ? "1"
              : "0.7"
          }
          bg={
            isExpanded && links.some((link) => location.pathname === link.Link)
              ? colors.brand.navPrimaryButton
              : "transparent"
          }
          _hover={{
            bg:
              isExpanded &&
              links.some((link) => location.pathname === link.Link)
                ? colors.brand.navSecondaryButton
                : colors.brand.navButtonHover,
            color: "white",
          }}
          borderRadius="md"
          px={3}
          py={2}
          color="white"
        >
          <Icon as={icon} boxSize={4} /> {title}{" "}
          <Icon
            as={
              isExpanded
                ? icons.utility.chevronDown
                : icons.utility.chevronRight
            }
            size="sm"
            ml="auto"
          />
        </Button>
        <Collapsible.Root open={isExpanded}>
          <Collapsible.Trigger asChild>
            <Box display="none" />
          </Collapsible.Trigger>
          <Collapsible.Content>
            <VStack gap={1} align="stretch" pl={4}>
              {links.map((nav) => (
                <Button
                  key={nav.Link}
                  fontSize="xs"
                  fontWeight="normal"
                  justifyContent="flex-start"
                  variant="ghost"
                  asChild
                  opacity={location.pathname === nav.Link ? "1" : "0.7"}
                  bg={
                    location.pathname === nav.Link
                      ? colors.brand.navSecondaryButton
                      : "transparent"
                  }
                  _hover={{
                    bg:
                      location.pathname === nav.Link
                        ? colors.brand.navSecondaryButton
                        : colors.brand.navButtonHover,
                    color: "white",
                  }}
                  borderRadius="sm"
                  px={3}
                  py={1}
                  color="white"
                >
                  <Link to={nav.Link}>
                    <Icon as={nav.Icon} boxSize={4} /> {nav.Name}
                  </Link>
                </Button>
              ))}
            </VStack>
          </Collapsible.Content>
        </Collapsible.Root>
      </VStack>
    );
  };

  return (
    <Box
      w={isCollapsed ? "70px" : "220px"}
      bg={colors.background.navBar}
      transition="width 0.3s ease"
      borderRight="1px"
      borderRightColor={"border"}
      h="100vh"
      overflow="hidden"
    >
      <Flex
        flexDirection="column"
        h="100%"
        position="relative"
        justifyContent="space-between"
      >
        {/* Top Section - Logo and Core Navigation */}
        <VStack
          align={isCollapsed ? "center" : "left"}
          px={2}
          pt={4}
          gap={4}
          flexShrink={0}
        >
          {/* Logo Section */}
          <Flex
            alignItems="center"
            gap={3}
            pl={isCollapsed ? "15px" : "3"}
            mb={2}
            w="full"
          >
            {isMobile && <MobileMenu isOpen={open} onClose={onClose} />}

            <Tooltip
              content="Go to Dashboard"
              positioning={{ placement: "bottom" }}
              showArrow
            >
              <Box
                cursor="pointer"
                onClick={handleLogoClick}
                transition="transform 0.2s"
                _hover={{ transform: "scale(1.05)" }}
                _active={{ transform: "scale(0.95)" }}
              >
                <Image
                  src={isCollapsed ? FavIcon : darkModeLogo}
                  alt="Result.ng Logo"
                  w={isCollapsed ? "25px" : "100px"}
                  h="auto"
                  // filter={
                  //   school?.accentColor !== "brand"
                  //     ? "grayscale(100%)"
                  //     : undefined
                  // }
                  objectFit="contain"
                />
              </Box>
            </Tooltip>
          </Flex>

          {/* Toggle Button */}
          <Button
            fontSize="xs"
            variant="subtle"
            position="absolute"
            right={isCollapsed ? -1 : -1}
            bottom="120px"
            justifyContent="center"
            onClick={toggleSidebar}
            size="2xs"
            opacity={isCollapsed ? "0.5" : "1"}
            zIndex={10}
            p={0}
          >
            {isCollapsed ? (
              <Icon as={icons.utility.chevronRight} />
            ) : (
              <Icon as={icons.utility.chevronLeft} />
            )}
          </Button>

          {/* Core Navigation */}
          <VStack gap={2} align="stretch" w="full">
            {coreNavLinks.map((nav) => (
              <Button
                key={nav.Name}
                fontSize={isCollapsed ? "xl" : "sm"}
                fontWeight="normal"
                justifyContent={isCollapsed ? "center" : "flex-start"}
                variant="ghost"
                asChild
                opacity={!isActiveLink(nav.Link) ? "0.7" : "1"}
                bg={
                  isActiveLink(nav.Link)
                    ? colors.brand.navPrimaryButton
                    : "transparent"
                }
                _hover={{
                  bg: isActiveLink(nav.Link)
                    ? colors.brand.navPrimaryButton
                    : colors.brand.navButtonHover,
                  color: "white",
                  transform: "scale(1.02)",
                }}
                _active={{
                  bg: colors.brand.navPrimaryButton,
                  color: "white",
                }}
                px={isCollapsed ? 2 : 3}
                py={2}
                color="white"
                transition="all 0.2s"
              >
                <Link to={nav.Link}>
                  <Icon as={nav.Icon} boxSize={4} />
                  {!isCollapsed && <Text>{nav.Name}</Text>}
                </Link>
              </Button>
            ))}
          </VStack>
          
        </VStack>

        {/* Middle Section - Scrollable Admin Navigation */}
        {isAdmin && showAdminNav && (
          <Box flex="1" overflowY="auto" css={noBarScrollBar} px={2} minH="0">
            <Separator borderColor="white" opacity={0.1} my={1} />
            <VStack gap={3} align="stretch" w="full" py={2}>
              {/* Admin Hub Sub-groups */}
              {!isCollapsed && (
                <VStack gap={2} align="stretch" w="full" pl={2}>
                  <Text color="white" fontSize="xs" opacity={0.7}>
                    ADMIN
                  </Text>
                  {renderAdminHubGroup(
                    "School Management",
                    schoolManagementLinks,
                    "school",
                    icons.main.school,
                    false
                  )}

                  {renderAdminHubGroup(
                    "Academics",
                    academicManagementLinks,
                    "academic",
                    icons.main.academic,
                    expandedGroups.academic
                  )}

                  {renderAdminHubGroup(
                    "User Management",
                    userManagementLinks,
                    "users",
                    icons.utility.shield,
                    expandedGroups.users
                  )}

                  {renderAdminHubGroup(
                    "Data Operations",
                    dataOperationsLinks,
                    "data",
                    icons.utility.upload,
                    expandedGroups.data
                  )}

                  {renderAdminHubGroup(
                    "Promotion System",
                    promotionLinks,
                    "promotion",
                    icons.main.promotion,
                    false
                  )}
                </VStack>
              )}

              {/* Collapsed Admin Hub - Show all sub-items as icons */}
              {isCollapsed && (
                <VStack gap={1} align="center">
                  {[
                    ...schoolManagementLinks,
                    ...academicManagementLinks,
                    ...userManagementLinks,
                    ...dataOperationsLinks,
                    ...promotionLinks,
                  ].map((nav) => (
                    <Tooltip
                      content={nav.Name}
                      positioning={{ placement: "right" }}
                      showArrow
                      key={nav.Link}
                    >
                      <Button
                        asChild
                        size="sm"
                        variant="ghost"
                        color="white"
                        opacity={location.pathname === nav.Link ? "1" : "0.7"}
                        bg={
                          location.pathname === nav.Link
                            ? colors.brand.navSecondaryButton
                            : "transparent"
                        }
                        _hover={{ bg: colors.brand.navButtonHover }}
                      >
                        <Link to={nav.Link}>
                          <Icon as={nav.Icon} size="sm" />
                        </Link>
                      </Button>
                    </Tooltip>
                  ))}
                </VStack>
              )}
            </VStack>
          </Box>
        )}

        {/* Bottom Section - Admin Toggle Button */}
        <VStack
          align={isCollapsed ? "center" : "left"}
          px={2}
          pb={4}
          gap={4}
          flexShrink={0}
        >
          {/* Admin Toggle Button - Only for admins when admin nav is closed */}
          {isAdmin && !showAdminNav && (
            <>
              <Separator opacity={0.1} borderColor="white" my={1} />
              <Button
                fontSize="sm"
                fontWeight="normal"
                justifyContent={isCollapsed ? "center" : "flex-start"}
                variant="ghost"
                onClick={toggleAdminNav}
                opacity={isInAdminSection() ? "1" : "0.7"}
                bg={
                  isInAdminSection()
                    ? colors.brand.navPrimaryButton
                    : "transparent"
                }
                _hover={{
                  bg: isInAdminSection()
                    ? colors.brand.navPrimaryButton
                    : colors.brand.navButtonHover,
                  color: "white",
                }}
                borderRadius="md"
                px={3}
                py={2}
                color="white"
              >
                <Flex gap={2} alignItems="center">
                  <Icon as={icons.utility.settings} boxSize={4} />
                  {!isCollapsed && "Admin"}
                </Flex>
              </Button>
            </>
          )}
        </VStack>
      </Flex>
    </Box>
  );
}

export default NavMenu;
